import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { CartPersistence, type CartItem } from '../modules/cart-persistence';
import { requestJSON } from '../core/http';
import { emitObservation } from '../core/observability';
import { useSessionStore } from './session-store';
import { useUserStore } from './user-store';
import { useOrdersStore } from './orders-store';
import { createLogger } from '@shared/utils/logger';
import { generateUuid } from '@shared/utils/uuid';

const log = createLogger('CartStore');
const CART_STATUS_ACTIVE = 'active';
const CART_STATUS_SUBMITTED = 'submitted';
const CART_STATUS_ABANDONED = 'abandoned';

type CartStatus =
  | typeof CART_STATUS_ACTIVE
  | typeof CART_STATUS_SUBMITTED
  | typeof CART_STATUS_ABANDONED;

type SubmitCartOptions = {
  notes?: string;
  tableNumber?: string | null;
  submitOrigin?: string;
};

type SubmitCartResponse = {
  order_id?: string | number;
  display_order_id?: string;
  session_id?: string | number;
  idempotent_replay?: boolean;
  [key: string]: unknown;
};

function stableStringify(value: unknown): string {
  const normalize = (input: unknown): unknown => {
    if (Array.isArray(input)) {
      return input.map((entry) => normalize(entry));
    }
    if (input && typeof input === 'object') {
      const obj = input as Record<string, unknown>;
      return Object.keys(obj)
        .sort()
        .reduce<Record<string, unknown>>((acc, key) => {
          acc[key] = normalize(obj[key]);
          return acc;
        }, {});
    }
    return input;
  };
  return JSON.stringify(normalize(value));
}

function itemMenuId(item: CartItem): string {
  return String(item.menu_item_id ?? item.id ?? '').trim();
}

function buildItemSignature(item: CartItem): string {
  const signaturePayload = {
    menu_item_id: itemMenuId(item),
    modifiers: item.modifiers || [],
    modifier_none_groups: item.modifier_none_groups || [],
    package_components: item.package_components || [],
    special_instructions: String(item.special_instructions || '').trim(),
  };
  return stableStringify(signaturePayload);
}

function toOrderItemPayload(item: CartItem) {
  return {
    menu_item_id: itemMenuId(item),
    quantity: Number(item.quantity || 1),
    modifiers: Array.isArray(item.modifiers) ? item.modifiers : [],
    modifier_none_groups: Array.isArray(item.modifier_none_groups)
      ? item.modifier_none_groups
      : [],
    package_components: Array.isArray(item.package_components)
      ? item.package_components
      : [],
    special_instructions: String(item.special_instructions || '').trim(),
  };
}

function createRandomId(): string {
  // Issue #878: fall back safely when crypto.randomUUID is unavailable
  // (plain http://<ip> deployments have no secure context).
  return generateUuid();
}

export const useCartStore = defineStore('client-cart', () => {
  const persistence = CartPersistence.getInstance();
  const sessionStore = useSessionStore();

  const items = ref<CartItem[]>(persistence.getCart());
  const isOpen = ref(false);
  const status = ref<CartStatus>(CART_STATUS_ACTIVE);
  const submitting = ref(false);
  const lastSubmittedOrderId = ref<string | null>(null);

  const totalItems = computed(() => {
    return items.value.reduce((sum, item) => sum + item.quantity, 0);
  });

  const totalPrice = computed(() => {
    return items.value.reduce((sum, item) => {
      return sum + (item.price + item.extrasTotal) * item.quantity;
    }, 0);
  });

  const isEmpty = computed(() => items.value.length === 0);

  const orderPayloadItems = computed(() => {
    return items.value.map((item) => toOrderItemPayload(item));
  });

  const cartFingerprint = computed(() => {
    const canonicalItems = orderPayloadItems.value
      .map((item) => ({
        ...item,
        modifiers: [...item.modifiers].sort((a, b) =>
          stableStringify(a).localeCompare(stableStringify(b)),
        ),
      }))
      .sort((a, b) => {
        if (a.menu_item_id === b.menu_item_id) {
          return stableStringify(a).localeCompare(stableStringify(b));
        }
        return a.menu_item_id.localeCompare(b.menu_item_id);
      });
    return stableStringify(canonicalItems);
  });

  const replaceCart = (nextItems: CartItem[]) => {
    persistence.setCart(nextItems);
    items.value = persistence.getCart();
  };

  const replaceItems = (nextItems: CartItem[]) => {
    replaceCart(nextItems);
    status.value = CART_STATUS_ACTIVE;
    isOpen.value = false;
  };

  const syncRemoteCart = async (
    nextStatus: CartStatus,
    notes: string = '',
    tableNumber: string | null = null,
    retryCount = 0,
    maxRetries = 3,
  ): Promise<void> => {
    if (!sessionStore.sessionReady) return;

    try {
      await requestJSON('/api/customer/orders/cart', {
        method: 'PUT',
        body: {
          status: nextStatus,
          notes: String(notes || ''),
          table_number: tableNumber || null,
          items: items.value.map((item) => toOrderItemPayload(item)),
        },
      });
      log.info('Cart synced successfully to backend');
    } catch (error) {
      const attemptNumber = retryCount + 1;
      log.warn(`Cart sync attempt ${attemptNumber}/${maxRetries} failed`, error);
      
      // FIX-003: Retry automático con exponential backoff
      if (retryCount < maxRetries) {
        const delayMs = Math.min(1000 * Math.pow(2, retryCount), 5000);
        log.info(`Retrying cart sync in ${delayMs}ms...`);
        
        await new Promise((resolve) => setTimeout(resolve, delayMs));
        return syncRemoteCart(nextStatus, notes, tableNumber, retryCount + 1, maxRetries);
      }
      
      // Después de 3 intentos fallidos, loguear error pero no bloquear UX
      log.error('Cart sync failed after 3 attempts - cart may be inconsistent', error);
    }
  };

  const addItem = (item: CartItem) => {
    const signature = buildItemSignature(item);
    const existingIndex = items.value.findIndex(
      (candidate) => buildItemSignature(candidate) === signature,
    );

    if (existingIndex >= 0) {
      const nextItems = [...items.value];
      nextItems[existingIndex] = {
        ...nextItems[existingIndex],
        quantity:
          Number(nextItems[existingIndex].quantity || 0) +
          Number(item.quantity || 1),
      };
      replaceCart(nextItems);
    } else {
      const normalizedItem: CartItem = {
        ...item,
        menu_item_id: item.menu_item_id ?? item.id,
        quantity: Number(item.quantity || 1),
      };
      replaceCart([...items.value, normalizedItem]);
    }

    status.value = CART_STATUS_ACTIVE;
    isOpen.value = true;
    void syncRemoteCart(CART_STATUS_ACTIVE);
  };

  const updateItem = (index: number, updatedItem: CartItem) => {
    const nextItems = [...items.value];
    if (index < 0 || index >= nextItems.length) return;
    const normalizedItem: CartItem = {
      ...updatedItem,
      menu_item_id: updatedItem.menu_item_id ?? updatedItem.id,
      quantity: Number(updatedItem.quantity || 1),
    };
    nextItems[index] = normalizedItem;
    replaceCart(nextItems);
    status.value = CART_STATUS_ACTIVE;
    void syncRemoteCart(CART_STATUS_ACTIVE);
  };

  const updateItemQuantity = (index: number, delta: number) => {
    const nextItems = [...items.value];
    const current = nextItems[index];
    if (!current) return;

    const quantity = Number(current.quantity || 0) + Number(delta || 0);
    if (quantity <= 0) {
      nextItems.splice(index, 1);
    } else {
      nextItems[index] = { ...current, quantity };
    }

    replaceCart(nextItems);
    status.value = CART_STATUS_ACTIVE;
    void syncRemoteCart(CART_STATUS_ACTIVE);
  };

  const removeItem = (index: number) => {
    const nextItems = [...items.value];
    if (index < 0 || index >= nextItems.length) return;
    nextItems.splice(index, 1);
    replaceCart(nextItems);
    status.value = CART_STATUS_ACTIVE;
    void syncRemoteCart(CART_STATUS_ACTIVE);
  };

  const clearCart = (nextStatus: CartStatus = CART_STATUS_ACTIVE) => {
    replaceCart([]);
    status.value = nextStatus;
  };

  const abandonCart = () => {
    replaceCart([]);
    status.value = CART_STATUS_ABANDONED;
    if (!sessionStore.sessionReady) return;

    void requestJSON('/api/customer/orders/cart/abandon', { method: 'POST' }).catch((error) => {
      log.warn('Unable to abandon cart in backend', error);
    });
  };

  const toggleCart = () => {
    isOpen.value = !isOpen.value;
  };

  const closeCart = () => {
    isOpen.value = false;
  };

  const openCart = () => {
    isOpen.value = true;
  };

  const refreshFromPersistence = () => {
    items.value = persistence.getCart();
  };

  const submitCart = async (
    options: SubmitCartOptions = {},
  ): Promise<SubmitCartResponse> => {
    const submitOrigin = String(options.submitOrigin || 'unknown').trim() || 'unknown';
    const sessionId = String(sessionStore.sessionId || '').trim() || null;
    const userStore = useUserStore();

    if (submitting.value) {
      emitObservation('submit_blocked', {
        submit_blocked_by_guard: true,
        submit_origin: submitOrigin,
        session_id: sessionId,
      });
      throw new Error('El envío ya está en progreso');
    }

    if (!userStore.isAuthenticated && (!sessionStore.sessionReady || !sessionId)) {
      emitObservation('submit_blocked', {
        submit_blocked_by_guard: true,
        submit_origin: submitOrigin,
        session_id: sessionId,
        reason: 'not_authenticated_no_session',
      });
      throw new Error('Debes iniciar sesión o seleccionar una mesa para confirmar el pedido');
    }

    if (!sessionStore.sessionReady || !sessionId) {
      emitObservation('submit_blocked', {
        submit_blocked_by_guard: true,
        submit_origin: submitOrigin,
        session_id: sessionId,
        reason: 'session_not_ready',
      });
      throw new Error('La sesión no está lista para enviar el pedido');
    }

    if (items.value.length === 0) {
      emitObservation('submit_blocked', {
        submit_blocked_by_guard: true,
        submit_origin: submitOrigin,
        session_id: sessionId,
        reason: 'empty_cart',
      });
      throw new Error('El carrito está vacío');
    }

    submitting.value = true;
    status.value = CART_STATUS_ACTIVE;

    const notes = String(options.notes || '').trim();
    const tableNumber = options.tableNumber || null;
    const idempotencyKey = `cart-submit-${createRandomId()}`;
    const requestId = `req-${createRandomId()}`;
    const submitStartedAt = new Date().toISOString();

    emitObservation('submit_started', {
      submit_origin: submitOrigin,
      session_id: sessionId,
      idempotency_key: idempotencyKey,
      request_id: requestId,
      submit_started_at: submitStartedAt,
      submit_blocked_by_guard: false,
    });

    await syncRemoteCart(CART_STATUS_ACTIVE, notes, tableNumber);

    try {
      const response = await requestJSON<SubmitCartResponse>('/api/v1/orders', {
        method: 'POST',
        headers: {
          'X-Idempotency-Key': idempotencyKey,
          'X-Request-ID': requestId,
        },
        body: {
          notes,
          table_id: tableNumber,
          items: orderPayloadItems.value,
        },
      });

      // The canonical identifier is order_id. display_order_id is a visual
      // folio only and must never be promoted to canonical identity.
      const canonicalOrderId = String(response.order_id ?? '').trim();
      if (!canonicalOrderId) {
        log.error(
          'POST /api/v1/orders succeeded without canonical order_id ' +
            `(display_order_id=${String(response.display_order_id || '').trim() || 'none'})`,
        );
        emitObservation('submit_contract_error', {
          submit_origin: submitOrigin,
          session_id: sessionId,
          reason: 'missing_order_id',
          display_order_id: String(response.display_order_id || '').trim() || null,
        });
        throw new Error(
          'La respuesta del servidor no incluyó el identificador canónico de la orden (order_id)',
        );
      }

      status.value = CART_STATUS_SUBMITTED;
      lastSubmittedOrderId.value = canonicalOrderId;

      const ordersStore = useOrdersStore();
      ordersStore.addOrder({
        id: canonicalOrderId,
        display_id:
          String(response.display_order_id || response.order_id || '').trim() ||
          undefined,
        display_order_id: String(response.display_order_id || '').trim() || undefined,
        workflow_status: String(response.status || 'new').trim() || 'new',
        status: String(response.status || 'new').trim() || 'new',
        total_amount: Number(response.total_amount || totalPrice.value || 0),
        items: items.value.map((item, idx) => ({
          id: `${canonicalOrderId}-item-${idx}`,
          menu_item_id: item.menu_item_id ?? item.id,
          name: item.name || '',
          price: Number(item.price || 0),
          unit_price: Number(item.price || 0),
          quantity: Number(item.quantity || 1),
          extras: item.extras || [],
          modifiers: item.modifiers || [],
          special_instructions: item.special_instructions || '',
        })),
        optimistic: true,
        pending_reconciliation: true,
        optimistic_session_id: sessionId || undefined,
      });

      replaceCart([]);

      emitObservation('submit_resolved', {
        submit_origin: submitOrigin,
        session_id: sessionId,
        idempotency_key: idempotencyKey,
        request_id: requestId,
        submit_started_at: submitStartedAt,
        submit_resolved_at: new Date().toISOString(),
        submit_blocked_by_guard: false,
      });

      return response;
    } finally {
      submitting.value = false;
    }
  };

  persistence.addListener((newItems) => {
    items.value = newItems;
  });

  return {
    items,
    isOpen,
    totalItems,
    totalPrice,
    isEmpty,
    cartFingerprint,
    status,
    submitting,
    lastSubmittedOrderId,
    orderPayloadItems,
    addItem,
    updateItem,
    updateItemQuantity,
    removeItem,
    replaceItems,
    clearCart,
    abandonCart,
    submitCart,
    toggleCart,
    closeCart,
    openCart,
    refreshFromPersistence,
  };
});
